<template>
  <div class="Wdgt-Auth-Module">
    <form class="Wdgt-Auth-Module__form" ref="form">
      <TextInput class="Wdgt-Auth-Module__form-field" :placeholder="'Имя пользователя'" :name="NAME_USERNAME" :errorMessage="errorUsername" @update-value="clearErrorUsername" />
      <TextInput class="Wdgt-Auth-Module__form-field" :placeholder="'Отображаемое имя'" :name="NAME_FIRST_NAME" :errorMessage="errorFirstName"  @update-value="clearErrorFirstName"/>
      <TextInput class="Wdgt-Auth-Module__form-field" :placeholder="'E-mail адресс'" :name="NAME_EMAIL" :errorMessage="errorEmail" @update-value="clearErrorEmail" />
      <PassInput class="Wdgt-Auth-Module__form-field" :placeholder="'Пароль'" :name="NAME_PASS" :isError="get_isPassError" @update-value="clearErrorPass" />
      <PassInput class="Wdgt-Auth-Module__form-field" :placeholder="'Повторите пароль'" :name="NAME_PASS_REPEAT" :errorMessage="errorPass" @update-value="clearErrorPass" />
      <VueTurnstile
        class="Wdgt-Auth-Module__turnstile"
        v-model="turnstileToken"
        :siteKey="CLOUDFLARE_SITE_KEY"
        :theme="currentTheme"
        :options="CLOUDFLARE_OPTIONS"
        @verify="onVerify"
        @error="onError"
        @expire="onExpire"
      />
      <ButtonMain class="Wdgt-Auth-Module__button" :width="'100%'" @click="toSignUp" :loading="get_isLoading" :disabled="!turnstileToken">Создать аккаунт</ButtonMain>
      <p class="Wdgt-Auth-Module__help-txt">
        Создавая аккаунт, Вы принимаете
        <router-link class="Wdgt-Auth-Module__help-lnk" :to="{name: PATHS_NAME.RULES.TERMS}" @click="toCloseAllPopups">Условия использования</router-link>
        и
        <router-link class="Wdgt-Auth-Module__help-lnk" :to="{name: PATHS_NAME.RULES.PRIVACY}" @click="toCloseAllPopups">Политику конфиденциальности</router-link>
      </p>
      <p class="Wdgt-Auth-Module__ftr-txt">
        У Вас есть аккаунт? <button class="Wdgt-Auth-Module__lnk" @click="toOpenLoginSection" type="button">Войти</button>
      </p>
    </form>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { PATHS_NAME } from "@shared/routers/consts/pathsName";
  import { PROJECT_KEYS } from '@shared/constants/keys/keys';
  import { backendUrl } from "@shared/constants/urls/appUrls";
  import VueTurnstile from 'vue-turnstile';
  import AUTH_SECTIONS_NAME from '@shared/constants/dropdowns/authSectionsName.js';
  import DROPDOWN_NAMES from '@shared/constants/dropdowns/dropdownsName.js';
  //-------------------------------------
  import TextInput from '@shared/UI/Fields/TextInput/TextInput.vue';
  import PassInput from '@shared/UI/Fields/PassInput/PassInput.vue';
  import ButtonMain from '@shared/UI/Buttons/ButtonMain/ButtonMain.vue';
  //-------------------------------------
  const store = useStore();
  const router = useRouter();
  //-------------------------------------
  const NAME_EMAIL = 'email';
  const NAME_PASS = 'password';
  const NAME_PASS_REPEAT = 'password2';
  const NAME_USERNAME = 'username';
  const NAME_FIRST_NAME = 'first_name';
  const MAX_LENGTH_PASS = 8;
  const SIGNUP_ENDPOINT = `${ backendUrl }/api/auth/users/`;
  const CLOUDFLARE_SITE_KEY = PROJECT_KEYS.TURNSTILE_SITE_KEY;
  const CLOUDFLARE_OPTIONS = { size: 'normal' }
  //-------------------------------------
  const form = ref(null);
  const errorEmail = ref('');
  const errorPass = ref('');
  const errorUsername = ref('');
  const errorFirstName = ref('');
  const turnstileToken = ref(null);
  //-------------------------------------
  onMounted(() => {
    window.addEventListener('keydown', onEnterKeyPress);
  });
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', onEnterKeyPress);
  });
  //-------------------------------------
  //Статус отправки данных
  const get_isLoading = computed(() => store.state.AuthorizationSystem.isLoading)
  //Есть ли ошибка поля с паролем
  const get_isPassError = computed(() => errorPass.value !== '');
  //Получаем текущую цветовую тему
  const currentTheme = computed(() => {
    let theme = localStorage.getItem('color_mode');
    return (!theme) ? 'light' : theme;
  });
  //-------------------------------------
  //Начать процесс регистрации
  const toSignUp = async () => {
    if(get_isLoading.value) return;
    toChangeLoadingFlag(true);
    try {
      const formObj = new FormData(form.value);
      const isCorrectPass = toCheckPass(formObj);
      if(!isCorrectPass){
        toChangeLoadingFlag(false);
        return;
      }
      const response = await fetch(SIGNUP_ENDPOINT, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(toPrepareData())
      });
      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json();
          errorUsername.value = errorData.username ? errorData.username[0] : '';
          errorEmail.value = errorData.email ? errorData.email[0] : '';
          errorPass.value = errorData.password ? errorData.password[0] : '';
        }
        toChangeLoadingFlag(false);
        throw new Error(`Ошибка сети: ${response.status} - ${response.statusText}`);
      }
      const data = await response.json();
      // Сохранение токенов перенесено в ConfirmEmail
      // await store.dispatch('saveAccessToken', data.tokens.access);
      // setCookie('refresh_token', data.tokens.refresh, cookieExpiresDays);
      toChangeLoadingFlag(false);
      toCloseWidget();
      localStorage.setItem('confirm_email', data.email); // Сохранение email в localStorage
      await router.push({ name: PATHS_NAME.PENDING_CONFIRM_EMAIL });
    } catch(error){
      toChangeLoadingFlag(false);
      console.error('Ошибка при попытке регистрации:', error);
    }
  }
  //Проверить пароли
  const toCheckPass = (formData) => {
    const username = formData.get(NAME_USERNAME);
    const email = formData.get(NAME_EMAIL);
    const firstName = formData.get(NAME_FIRST_NAME);
    const pass = formData.get(NAME_PASS);
    const pass2 = formData.get(NAME_PASS_REPEAT);
    if(username.length < 1){
      errorUsername.value = 'Это обязательное поле';
      return false;
    }
    if(firstName.length < 1){
      errorUsername.value = 'Это обязательное поле';
      return false;
    }
    if(email.length < 1){
      errorEmail.value = 'Это обязательное поле';
      return false;
    }
    if(pass !== pass2){
      errorPass.value = 'Пароли не совпадают';
      return false;
    }
    if(pass.length < MAX_LENGTH_PASS){
      errorPass.value = `Пароль слишком короткий (минимум ${MAX_LENGTH_PASS} символов)`;
      return false;
    }
    return true;
  }
  //Подготовить данные для отправки на сервер
  const toPrepareData = () => {
    const formObj = new FormData(form.value);
    const dataForServer = {};
    for (const [key, value] of formObj.entries()) {
      // Пропускаем поле 'cf-turnstile-response'
      if (key === 'cf-turnstile-response') {
        continue;
      }
      dataForServer[key] = value;
    }
    // Добавляем токен Turnstile, если он существует
    if (turnstileToken.value) {
      dataForServer['turnstile_token'] = turnstileToken.value;
    }
    return dataForServer;
  }
  //Очистить все дропдауны при переходе на страницу
  const toCloseAllPopups = () => {
    store.dispatch('Dropdowns/clearAllDropdowns');
  }
  //Открыть стартовую секцию регистрации
  const toOpenLoginSection = () => {
    store.commit('AuthorizationSystem/toActiveSection', AUTH_SECTIONS_NAME.START_LOGIN);
  }
  //Закрыть текущее модальное окно
  const toCloseWidget = () => store.dispatch('Dropdowns/removeTheDropdown', DROPDOWN_NAMES.auth);
  //Отметить процесс отправки данных на сервер
  const toChangeLoadingFlag = (status) => store.commit('AuthorizationSystem/toChangeLoadingFlag', status);
  //Очистить ошибку пароля
  const clearErrorPass = () => errorPass.value = '';
  //Очистить ошибку поля имя пользователя
  const clearErrorUsername = () => errorUsername.value = '';
  //Очистить поле почты
  const clearErrorEmail = () => errorEmail.value = '';
  //Глобальный обработчик для Enter
  const onEnterKeyPress = (e) => {
    const key = e.key || e.keyCode;
    if (key === 'Enter' || key === 13) {
      e.preventDefault();
      toSignUp();
    }
  };
  //Очистить поле никнейм
  const clearErrorFirstName = () => errorFirstName.value = '';
  //Вызывается при успешной проверке Turnstile, устанавливает полученный токен в реактивную переменную `turnstileToken`.
  const onVerify = (token) => turnstileToken.value = token;
  //Вызывается при возникновении ошибки в Turnstile. Логирует ошибку в консоль и очищает переменную `turnstileToken`.
  const onError = (error) => {
    console.error('Turnstile error:', error)
    turnstileToken.value = null;
  }
  //Вызывается при истечении срока действия токена Turnstile. Очищает переменную `turnstileToken`, чтобы токен больше не считался валидным.
  const onExpire = () => turnstileToken.value = null;
</script>

<style lang="scss">
  @import '../../styles/Wdgt-Auth-Module.scss';
</style>
